import React, { useEffect, useState } from 'react';
import { supabase } from '../../supabaseClient';
import '../AdminShared.css';

const emptyForm = {
  title: '', release_type: 'Single', release_date: '',
  spotify_url: '', apple_url: '', deezer_url: '', cover_url: '',
};

export default function ReleasesTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(null); // null | 'new' | release object
  const [form, setForm] = useState(emptyForm);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('releases')
      .select('*')
      .order('sort_order', { ascending: true })
      .order('created_at', { ascending: false });
    if (error) setError(error.message);
    else setItems(data);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(emptyForm); setEditing('new'); };
  const openEdit = (r) => { setForm(r); setEditing(r.id); };
  const closeForm = () => { setEditing(null); setForm(emptyForm); };

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    const fileName = `releases/${Date.now()}-${file.name}`;
    const { error } = await supabase.storage.from('images').upload(fileName, file);
    if (error) {
      setError(error.message);
      setUploading(false);
      return;
    }
    const { data } = supabase.storage.from('images').getPublicUrl(fileName);
    setForm((f) => ({ ...f, cover_url: data.publicUrl }));
    setUploading(false);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');

    const payload = {
      title: form.title,
      release_type: form.release_type,
      release_date: form.release_date || null,
      cover_url: form.cover_url,
      spotify_url: form.spotify_url,
      apple_url: form.apple_url,
      deezer_url: form.deezer_url,
    };

    const { error } = editing === 'new'
      ? await supabase.from('releases').insert(payload)
      : await supabase.from('releases').update(payload).eq('id', editing);

    setSaving(false);

    if (error) { setError(error.message); return; }
    closeForm();
    load();
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this release?')) return;
    const { error } = await supabase.from('releases').delete().eq('id', id);
    if (error) setError(error.message);
    else load();
  };

  return (
    <div>
      <div className="tab-header">
        <h2 className="tab-title">Music Releases</h2>
        {!editing && <button className="tab-add-btn" onClick={openNew}>+ Add release</button>}
      </div>

      {error && <p className="tab-error">{error}</p>}

      {editing && (
        <form className="admin-form" onSubmit={handleSave}>
          <div className="admin-form-row">
            <label className="admin-form-label">Title</label>
            <input
              className="admin-form-input" required
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
            />
          </div>

          <div className="admin-form-row">
            <label className="admin-form-label">Type</label>
            <input
              className="admin-form-input"
              value={form.release_type}
              onChange={(e) => setForm({ ...form, release_type: e.target.value })}
              placeholder="Single, EP, Album..."
            />
          </div>

          <div className="admin-form-row">
            <label className="admin-form-label">Release date</label>
            <input
              type="date" className="admin-form-input"
              value={form.release_date || ''}
              onChange={(e) => setForm({ ...form, release_date: e.target.value })}
            />
          </div>

          <div className="admin-form-row">
            <label className="admin-form-label">Cover image</label>
            <input type="file" accept="image/*" onChange={handleUpload} />
            {uploading && <span className="tab-status">Uploading...</span>}
            {form.cover_url && (
              <img src={form.cover_url} alt="cover preview" className="admin-image-preview" />
            )}
          </div>

          <div className="admin-form-row">
            <label className="admin-form-label">Spotify URL</label>
            <input
              className="admin-form-input"
              value={form.spotify_url || ''}
              onChange={(e) => setForm({ ...form, spotify_url: e.target.value })}
            />
          </div>

          <div className="admin-form-row">
            <label className="admin-form-label">Apple Music URL</label>
            <input
              className="admin-form-input"
              value={form.apple_url || ''}
              onChange={(e) => setForm({ ...form, apple_url: e.target.value })}
            />
          </div>

          <div className="admin-form-row">
            <label className="admin-form-label">Deezer URL</label>
            <input
              className="admin-form-input"
              value={form.deezer_url || ''}
              onChange={(e) => setForm({ ...form, deezer_url: e.target.value })}
            />
          </div>

          <div className="admin-form-actions">
            <button className="admin-form-save" disabled={saving || uploading} type="submit">
              {saving ? 'Saving...' : 'Save'}
            </button>
            <button className="admin-form-cancel" type="button" onClick={closeForm}>Cancel</button>
          </div>
        </form>
      )}

      {loading ? (
        <p className="tab-status">Loading...</p>
      ) : items.length === 0 ? (
        <p className="tab-status">No releases yet.</p>
      ) : (
        items.map((r) => (
          <div className="item-card" key={r.id}>
            <img src={r.cover_url} alt={r.title} className="item-thumb" />
            <div className="item-info">
              <p className="item-title">{r.title}</p>
              <p className="item-sub">{r.release_type} {r.release_date ? `• ${r.release_date}` : ''}</p>
            </div>
            <div className="item-actions">
              <button className="item-btn" onClick={() => openEdit(r)}>Edit</button>
              <button className="item-btn danger" onClick={() => handleDelete(r.id)}>Delete</button>
            </div>
          </div>
        ))
      )}
    </div>
  );
}
